(() => {
  if (customElements.get('sticky-add-to-cart')) return;

  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.variants = this.parseJSON('[data-sticky-product-variants]', []);
        this.form = this.querySelector('form[action*="/cart/add"]');
        this.variantIdInput = this.querySelector('[name="id"]');
        this.selects = [...this.querySelectorAll('[data-option-position]')];
        this.quantityInput = this.querySelector('.sticky-atc__quantity-input');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.submitLabel = this.querySelector('[data-sticky-submit-label]');
        this.currentPriceNode = this.querySelector('[data-sticky-current-price]');
        this.comparePriceNode = this.querySelector('[data-sticky-compare-price]');
        this.savingsNode = this.querySelector('[data-sticky-savings]');
        this.toggleButton = this.querySelector('[data-sticky-toggle]');
        this.drawer = this.querySelector('[data-sticky-drawer]');

        this.bindOptionEvents();
        this.bindQuantityEvents();
        this.bindDrawerEvents();
        this.bindExternalVariantEvents();
        this.bindVisibility();

        const selectedVariant = this.findVariantById(this.variantIdInput?.value) || this.firstPurchasableVariant();
        this.applyVariant(selectedVariant, { updateSelects: true, updateUrl: false });
        window.setTimeout(() => this.removeAttribute('hidden'), 0);
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        document.removeEventListener('change', this.handleDocumentChange);
        document.removeEventListener('variantChange', this.handleVariantChange);
        this.removeEventListener('keydown', this.handleDrawerKeydown);
      }

      parseJSON(selector, fallback) {
        try {
          return JSON.parse(this.querySelector(selector)?.textContent || '');
        } catch (error) {
          return fallback;
        }
      }

      bindOptionEvents() {
        this.selects.forEach((select) => {
          select.addEventListener('change', () => {
            const variant = this.findSelectedVariant() || this.findClosestAvailableVariant();
            this.applyVariant(variant, { updateSelects: Boolean(variant), updateUrl: true });
          });
        });
      }

      bindQuantityEvents() {
        this.querySelector('[data-sticky-quantity-minus]')?.addEventListener('click', () => this.stepQuantity(-1));
        this.querySelector('[data-sticky-quantity-plus]')?.addEventListener('click', () => this.stepQuantity(1));
        this.quantityInput?.addEventListener('change', () => this.normalizeQuantity());
      }

      bindDrawerEvents() {
        if (!this.toggleButton || !this.drawer) return;

        this.toggleButton.addEventListener('click', () => {
          this.setDrawerOpen(this.drawer.hidden);
        });

        this.handleDrawerKeydown = (event) => {
          if (event.key === 'Escape' && !this.drawer.hidden) {
            this.setDrawerOpen(false);
            this.toggleButton.focus();
          }
        };

        this.addEventListener('keydown', this.handleDrawerKeydown);
      }

      bindExternalVariantEvents() {
        this.handleVariantChange = (event) => {
          if (this.contains(event.target)) return;
          const variant = event.detail?.variant || event.detail?.data?.variant;
          if (variant?.id) this.applyVariant(this.findVariantById(variant.id), { updateSelects: true, updateUrl: false });
        };

        this.handleDocumentChange = (event) => {
          if (this.contains(event.target) || event.target?.name !== 'id') return;
          const variant = this.findVariantById(event.target.value);
          if (variant) this.applyVariant(variant, { updateSelects: true, updateUrl: false });
        };

        document.addEventListener('variantChange', this.handleVariantChange);
        document.addEventListener('change', this.handleDocumentChange);
      }

      bindVisibility() {
        if (this.dataset.visibilityMode === 'always') {
          this.classList.remove('is-hidden');
          return;
        }

        const target = document.querySelector(this.dataset.showAfterSelector || '.product-form__buttons');
        if (!target || !('IntersectionObserver' in window)) {
          this.classList.remove('is-hidden');
          return;
        }

        this.classList.add('is-hidden');
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.classList.toggle('is-hidden', entry.isIntersecting);
          },
          { threshold: 0.05 }
        );
        this.intersectionObserver.observe(target);
      }

      setDrawerOpen(open) {
        if (!this.drawer || !this.toggleButton) return;
        this.drawer.hidden = !open;
        this.classList.toggle('is-open', open);
        this.toggleButton.setAttribute('aria-expanded', String(open));
      }

      findSelectedVariant() {
        const optionValues = this.selects.map((select) => select.value);
        return this.variants.find((variant) => {
          return variant.options?.every((value, index) => value === optionValues[index]);
        });
      }

      findClosestAvailableVariant() {
        const optionValues = this.selects.map((select) => select.value);
        return (
          this.variants.find((variant) => {
            return variant.available && variant.options?.every((value, index) => {
              return index === 0 ? value === optionValues[index] : true;
            });
          }) ||
          this.variants.find((variant) => variant.available) ||
          this.findSelectedVariant()
        );
      }

      findVariantById(id) {
        return this.variants.find((variant) => String(variant.id) === String(id));
      }

      firstPurchasableVariant() {
        return this.variants.find((variant) => variant.available) || this.variants[0] || null;
      }

      applyVariant(variant, options = {}) {
        if (!variant) {
          this.setUnavailableState();
          return;
        }

        if (options.updateSelects) this.updateSelectValues(variant);
        this.updateOptionAvailability();
        this.updateVariantInput(variant);
        this.updatePrice(variant);
        this.updateQuantityRules(variant);
        this.updateSubmitState(variant);

        if (options.updateUrl && this.dataset.productUrl) {
          const url = `${this.dataset.productUrl}?variant=${variant.id}`;
          this.querySelectorAll('a[href]').forEach((link) => {
            link.href = url;
          });
        }
      }

      updateSelectValues(variant) {
        this.selects.forEach((select, index) => {
          if (variant.options?.[index] != null) select.value = variant.options[index];
        });
      }

      updateOptionAvailability() {
        this.selects.forEach((select, targetIndex) => {
          [...select.options].forEach((option) => {
            const canSelect = this.variants.some((variant) => {
              if (!variant.available || variant.options?.[targetIndex] !== option.value) return false;
              return this.selects.every((otherSelect, index) => {
                return index === targetIndex || variant.options?.[index] === otherSelect.value;
              });
            });
            option.disabled = !canSelect;
          });
        });
      }

      updateVariantInput(variant) {
        if (!this.variantIdInput) return;
        this.variantIdInput.value = variant.id;
        this.variantIdInput.disabled = !variant.available;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updatePrice(variant) {
        const price = Number(variant.price || 0);
        const compareAt = Number(variant.compare_at_price || 0);
        const hasComparePrice = compareAt > price;

        if (this.currentPriceNode) {
          this.currentPriceNode.textContent = this.formatMoney(price);
        }

        if (this.comparePriceNode) {
          this.comparePriceNode.textContent = this.formatMoney(compareAt);
          this.comparePriceNode.hidden = !hasComparePrice;
        }

        if (this.savingsNode) {
          const savings = Math.max(compareAt - price, 0);
          const shouldShowSavings = hasComparePrice || this.dataset.hideSavingsWithoutCompare !== 'true';
          this.savingsNode.textContent = this.formatSavings(savings);
          this.savingsNode.hidden = !shouldShowSavings;
        }
      }

      updateQuantityRules(variant) {
        if (!this.quantityInput) return;
        const rule = variant.quantity_rule || {};
        const min = Number(rule.min || 1);
        const increment = Number(rule.increment || 1);
        this.quantityInput.min = min;
        this.quantityInput.step = increment;
        if (rule.max == null) {
          this.quantityInput.removeAttribute('max');
        } else {
          this.quantityInput.max = Number(rule.max);
        }
        this.normalizeQuantity();
      }

      updateSubmitState(variant) {
        const available = Boolean(variant.available);
        if (this.submitButton) this.submitButton.disabled = !available;
        if (this.submitLabel) {
          this.submitLabel.textContent = available ? this.dataset.textAdd : this.dataset.textSoldOut;
        }
        this.form?.classList.toggle('is-sold-out', !available);
      }

      setUnavailableState() {
        if (this.variantIdInput) {
          this.variantIdInput.value = '';
          this.variantIdInput.disabled = true;
        }
        if (this.submitButton) this.submitButton.disabled = true;
        if (this.submitLabel) this.submitLabel.textContent = this.dataset.textUnavailable;
        if (this.comparePriceNode) this.comparePriceNode.hidden = true;
        if (this.savingsNode) this.savingsNode.hidden = true;
      }

      stepQuantity(direction) {
        if (!this.quantityInput) return;
        const step = Number(this.quantityInput.step || 1);
        const current = Number(this.quantityInput.value || this.quantityInput.min || 1);
        this.quantityInput.value = current + step * direction;
        this.normalizeQuantity();
      }

      normalizeQuantity() {
        if (!this.quantityInput) return;
        const min = Number(this.quantityInput.min || 1);
        const max = this.quantityInput.max ? Number(this.quantityInput.max) : null;
        const step = Number(this.quantityInput.step || 1);
        let value = Number(this.quantityInput.value || min);
        value = Math.max(min, value);
        if (max !== null) value = Math.min(max, value);
        value = min + Math.round((value - min) / step) * step;
        this.quantityInput.value = value;
      }

      formatSavings(cents) {
        const template = this.dataset.savingsLabel || 'save [amount]';
        return template.replace('[amount]', this.formatMoney(cents));
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const placeholder = /\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return (Number(cents) / 100).toFixed(2);

        const noDecimals = match[1].includes('no_decimals');
        const commaSeparator = match[1].includes('comma_separator');
        const options = noDecimals
          ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
        let value = (Number(cents) / 100).toLocaleString('en-US', options);
        if (commaSeparator) {
          value = value.replace(/,/g, '_').replace(/\./g, ',').replace(/_/g, '.');
        }
        return format.replace(placeholder, value);
      }
    }
  );
})();
